import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { money, signedMoney, moneyClass, cycleLabel, dateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Wallet, Users, WarningCircle, CurrencyDollar, ArrowsClockwise, ArrowRight,
} from "@phosphor-icons/react";

function Stat({ label, value, sub, icon: Icon, tone = "default", testid }) {
  const toneCls = tone === "danger" ? "text-destructive" : tone === "success" ? "text-[hsl(var(--success))]" : "text-foreground";
  return (
    <div data-testid={testid} className="rounded-sm border border-border bg-card p-5 transition-colors duration-200 hover:border-muted-foreground/40">
      <div className="mb-3 flex items-center justify-between">
        <span className="overline">{label}</span>
        <Icon size={18} className="text-muted-foreground" />
      </div>
      <div className={`font-mono text-3xl font-semibold tracking-tight ${toneCls}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/dashboard").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const runReconcile = async () => {
    setRunning(true);
    try {
      const r = await api.post("/reconcile");
      const n = r.data.exceptions.length;
      toast.success(n === 0 ? "All reconciled — no exceptions" : `Reconciled — ${n} exception(s) to review`);
      load();
    } catch (e) { toast.error("Reconciliation failed"); }
    finally { setRunning(false); }
  };

  if (!data) return <div className="text-muted-foreground font-mono text-sm">Loading…</div>;

  return (
    <div>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Cycle {cycleLabel(data.current_cycle)} · single source of truth for what every rep is owed</p>
        </div>
        <Button data-testid="run-reconciliation-button" onClick={runReconcile} disabled={running} className="gap-2">
          <ArrowsClockwise size={16} weight="bold" className={running ? "animate-spin" : ""} />
          {running ? "Reconciling…" : "Run Reconciliation"}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Stat testid="stat-owed" label="Commission Owed" value={money(data.total_owed)} sub="Payable + adjustments, this ledger" icon={Wallet}
          tone={data.total_owed < 0 ? "danger" : "default"} />
        <Stat testid="stat-paid" label="Paid To Date" value={money(data.total_paid)} sub="Cleared payouts" icon={CurrencyDollar} tone="success" />
        <Stat testid="stat-reps" label="Active Reps" value={`${data.active_count} / ${data.rep_count}`} sub="Currently earning" icon={Users} />
        <Stat testid="stat-exceptions" label="Open Exceptions" value={data.exceptions_count} sub="Needs a human" icon={WarningCircle}
          tone={data.exceptions_count > 0 ? "danger" : "success"} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-sm border border-border bg-card p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Last Reconciliation</h3>
            <button data-testid="goto-reconciliation" onClick={() => navigate("/reconciliation")}
              className="flex items-center gap-1 text-xs text-primary hover:underline">
              View exceptions <ArrowRight size={13} />
            </button>
          </div>
          {data.last_run ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div><div className="overline">Ran</div><div className="mt-1 font-mono text-sm">{dateTime(data.last_run.ran_at)}</div></div>
              <div><div className="overline">Reps</div><div className="mt-1 font-mono text-sm">{data.last_run.reps_processed}</div></div>
              <div><div className="overline">Booked</div><div className="mt-1 font-mono text-sm text-[hsl(var(--success))]">{data.last_run.events_booked}</div></div>
              <div><div className="overline">Reversed</div><div className="mt-1 font-mono text-sm text-destructive">{data.last_run.events_reversed}</div></div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No reconciliation has run yet. Click <span className="font-medium text-foreground">Run Reconciliation</span> to book the ledger.</p>
          )}
        </div>
        <div className="rounded-sm border border-border bg-card p-5">
          <h3 className="font-heading text-lg font-semibold">Cost Guardrail</h3>
          <div className="mt-4 overline">Active Monthly Rep Cost</div>
          <div className="mt-1 font-mono text-2xl font-semibold">{money(data.monthly_cost)}</div>
          <p className="mt-2 text-xs text-muted-foreground">Sum of monthly cost across active reps — protect margin as you scale to 50.</p>
        </div>
      </div>
    </div>
  );
}
